import { JSONPath } from "jsonpath-plus";
import {
  PolicyLevelCoverages,
  UnitLevelCoverages,
} from "@covertree/new-business-graphql";
import {
  Coverage,
  ScreenWithCoverages,
  PackageCoverageAccessor,
  ExtraCoverageAccessor,
  getExtraCoveragesMap,
  getPackageCoveragesMap,
  CoverageLevel,
  extraCoverageAccessors,
  packageCoverageAccessors,
  settlementCoverageAccessors,
} from "@covertree/new-business-mappings";
import {
  getCoverageWithSettlementItems,
  getMedicalPaymentsItems,
  getPersonalLiabilityItems,
  getPremisesLiabilityItems,
} from "./getItems";
import { sortBasedOnAnotherArr } from "@covertree/utils";

const availablePackageCoverages = [...packageCoverageAccessors]
  .filter(
    (accessor) =>
      !settlementCoverageAccessors.includes(accessor) &&
      accessor !== PackageCoverageAccessor.RoofPaymentSchedule
  )
  .sort((accessro1, accessor2) =>
    sortBasedOnAnotherArr(accessro1, accessor2, [
      PackageCoverageAccessor.HomeCoverage,
      PackageCoverageAccessor.YourBelongings,
      PackageCoverageAccessor.PersonalLiability,
      PackageCoverageAccessor.PremisesLiability,
      PackageCoverageAccessor.StandardDeductible,
      PackageCoverageAccessor.WindHail,
      PackageCoverageAccessor.WaterDamageDeductible,
      PackageCoverageAccessor.LossOfUse,
      PackageCoverageAccessor.OtherStructures,
      PackageCoverageAccessor.WaterDamage,
      PackageCoverageAccessor.MedicalPayments,
      PackageCoverageAccessor.InflationGuard,
    ])
  );

const availableExtraCoverages = [...extraCoverageAccessors].sort(
  (accessor1, accessor2) =>
    sortBasedOnAnotherArr(accessor1, accessor2, [
      ExtraCoverageAccessor.MineSubsidence,
      ExtraCoverageAccessor.MineSubsidenceScheduled,
      ExtraCoverageAccessor.GolfCartDamageAndLiabilityCoverage,
      ExtraCoverageAccessor.EarthquakeCoverage,
      ExtraCoverageAccessor.VacationRentalCoverage,
      ExtraCoverageAccessor.WaterBackupCoverage,
      ExtraCoverageAccessor.LossAssessment,
      ExtraCoverageAccessor.DebrisRemoval,
      ExtraCoverageAccessor.IncidentalFarming,
      ExtraCoverageAccessor.EnhancedCoverage,
      ExtraCoverageAccessor.TripCollisionCoverage,
      ExtraCoverageAccessor.EquipmentBreakdown,
      ExtraCoverageAccessor.IdentityFraudExpense,
      ExtraCoverageAccessor.ValuableItemProtection,
      ExtraCoverageAccessor.SinkholeCoverage,
      ExtraCoverageAccessor.ResidenceBurglary,
      ExtraCoverageAccessor.BuildersRisk,
      ExtraCoverageAccessor.SecondaryResidenceLiability,
      ExtraCoverageAccessor.VandalismOrMaliciousMischief,
      ExtraCoverageAccessor.WindstormOrHailExclusion,
      ExtraCoverageAccessor.FortifiedRoofUpgrade,
      ExtraCoverageAccessor.RoofPaymentSchedule,
    ])
);

type CoverageWithItems = Coverage & {
  items?: Record<string, string>[];
};

const getPlanOptions = (
  unitLevelCoverages: UnitLevelCoverages
): CoverageWithItems[] => {
  const coverageMap = getPackageCoveragesMap(ScreenWithCoverages.PolicySummary);

  const planOptions: CoverageWithItems[] = availablePackageCoverages.reduce(
    (accumulator, accessor) => {
      const coverage: CoverageWithItems =
        coverageMap[accessor as PackageCoverageAccessor];

      const coverageValue = JSONPath({
        path: accessor,
        json: unitLevelCoverages,
      })[0];

      const parentCoverage = JSONPath({
        path: accessor.split(".")[0],
        json: unitLevelCoverages,
      })[0];

      if (coverageValue) {
        if (
          accessor === PackageCoverageAccessor.HomeCoverage ||
          accessor === PackageCoverageAccessor.OtherStructures ||
          accessor === PackageCoverageAccessor.YourBelongings
        ) {
          coverage.items = getCoverageWithSettlementItems(parentCoverage);
        }

        if (accessor === PackageCoverageAccessor.MedicalPayments) {
          coverage.items = getMedicalPaymentsItems(parentCoverage);
        }

        if (accessor === PackageCoverageAccessor.PersonalLiability) {
          coverage.items = getPersonalLiabilityItems(
            parentCoverage,
            unitLevelCoverages
          );
        }

        if (accessor === PackageCoverageAccessor.PremisesLiability) {
          coverage.items = getPremisesLiabilityItems(
            parentCoverage,
            unitLevelCoverages
          );
        }

        accumulator.push(coverage);
      }

      return accumulator;
    },
    [] as CoverageWithItems[]
  );

  return planOptions;
};

const getAddons = (
  unitLevelCoverages: UnitLevelCoverages,
  policyLevelCoverages: PolicyLevelCoverages
): CoverageWithItems[] => {
  const coverageMap = getExtraCoveragesMap(ScreenWithCoverages.PolicySummary);

  const addons: CoverageWithItems[] = availableExtraCoverages.reduce(
    (accumulator, accessor) => {
      const coverage: CoverageWithItems =
        coverageMap[accessor as ExtraCoverageAccessor];

      const coverageValue = JSONPath({
        path: coverage.accessor,
        json:
          coverage.level === CoverageLevel.Unit
            ? unitLevelCoverages
            : policyLevelCoverages,
      })[0];

      if (coverageValue) {
        accumulator.push(coverage);
      }

      return accumulator;
    },
    [] as CoverageWithItems[]
  );

  return addons;
};

export { getPlanOptions, getAddons };
